import { Link } from '@tanstack/react-router';
import type { ItemCustoResponse } from '@triade/contracts';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { formatarCusto } from '@/lib/format';

/** Valor do seletor para "a ficha ativa" (o padrao): nao manda `fichaId` para a API. */
export const FICHA_ATIVA = '__ativa__';

const MOTIVO_SEM_CUSTO: Record<string, string> = {
  SEM_AQUISICAO: 'não tem custo de aquisição vigente nesta data',
  TIPO_SEM_CUSTO: 'não tem custo para precificar',
};

/**
 * "Custo do produto" da Formacao (Sessao 37): aparece assim que o produto e escolhido, antes de tabela e condicao. Mostra o custo e a composicao que a API
 * calculou na data (nada e calculado aqui), a ficha usada e, produto sem ficha ativa, o AVISO CLARO com o link para escolher a ficha (no lugar do erro).
 */
export function CustoProduto({ custo, carregando }: { custo: ItemCustoResponse | undefined; carregando: boolean }) {
  if (!custo) {
    return carregando ? (
      <p role="status" className="rounded-2xl border border-border bg-card px-4 py-3 text-small text-ink-muted" data-testid="custo-produto-carregando">
        Calculando o custo do produto…
      </p>
    ) : null;
  }

  if (custo.origem === 'SEM_CUSTO' && custo.motivo === 'SEM_FICHA_ATIVA') {
    return (
      <section role="alert" data-testid="aviso-sem-ficha" className="rounded-2xl border border-warning/40 bg-warning-bg px-4 py-3 text-small text-warning-ink">
        <div className="font-semibold">{custo.descricao} não tem ficha técnica ativa</div>
        <p className="mt-1">
          {custo.fichas.length > 0
            ? `O produto tem ${custo.fichas.length} ${custo.fichas.length === 1 ? 'versão' : 'versões'} de ficha, mas nenhuma está marcada como a ativa. Escolha qual vale para calcular o custo e o preço.`
            : 'O produto ainda não tem ficha técnica. Crie a ficha e marque a versão que vale para calcular o custo e o preço.'}
        </p>
        <Link to="/custos/fichas-tecnicas" search={{ itemId: custo.itemId } as never} className="mt-2 inline-block font-medium underline">
          Escolher a ficha técnica
        </Link>
      </section>
    );
  }

  if (custo.origem === 'SEM_CUSTO') {
    return (
      <section role="alert" data-testid="aviso-sem-custo" className="rounded-2xl border border-warning/40 bg-warning-bg px-4 py-3 text-small text-warning-ink">
        <span className="font-semibold">{custo.descricao}</span> {MOTIVO_SEM_CUSTO[custo.motivo ?? ''] ?? 'está sem custo'}.{' '}
        <Link to="/cadastros/itens/$id" params={{ id: custo.itemId }} search={{} as never} className="font-medium underline">
          Abrir o cadastro do item
        </Link>
      </section>
    );
  }

  if (custo.origem === 'ERRO') {
    return (
      <section role="alert" data-testid="aviso-erro-ficha" className="rounded-2xl border border-destructive/30 bg-danger-bg px-4 py-3 text-small text-danger-ink">
        <div className="font-semibold">Não foi possível calcular o custo da ficha{custo.fichaUsada ? ` (versão ${custo.fichaUsada.versao})` : ''}</div>
        {custo.mensagem && <p className="mt-1">{custo.mensagem}</p>}
      </section>
    );
  }

  const c = custo.composicao;
  const fonte = custo.origem === 'AQUISICAO' ? 'Custo de aquisição vigente' : `Ficha técnica · versão ${custo.fichaUsada?.versao ?? ''}${custo.fichaUsada?.ativa ? ' (ativa)' : ''}`;
  return (
    <section aria-label="Custo do produto" data-testid="custo-produto" className="rounded-2xl border border-border bg-card px-4 py-3">
      <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
        <div>
          <div className="text-small text-ink-muted">Custo do produto · {fonte}</div>
          <div className="tabular text-metric font-semibold" data-testid="custo-produto-total">
            {custo.custoUnitario ? formatarCusto(custo.custoUnitario) : '—'}
          </div>
        </div>
        {c && (
          <dl className="flex flex-wrap gap-x-6 gap-y-1 text-small" data-testid="composicao-produto">
            <div>
              <dt className="text-ink-muted">Materiais</dt>
              <dd className="tabular font-medium">{formatarCusto(c.materiais)}</dd>
            </div>
            <div>
              <dt className="text-ink-muted">Processo (etapas)</dt>
              <dd className="tabular font-medium">{formatarCusto(c.processo)}</dd>
            </div>
            <div>
              <dt className="text-ink-muted">Semiacabados</dt>
              <dd className="tabular font-medium">{formatarCusto(c.semiacabados)}</dd>
            </div>
          </dl>
        )}
      </div>
    </section>
  );
}

/**
 * Seletor de ficha da Formacao (Sessao 37): o padrao e a ficha ativa; outra versao serve so para SIMULAR. Item comprado ou sem fichas: so o texto
 * da fonte do custo.
 */
export function SeletorFicha({ custo, valor, onChange, desabilitado }: { custo: ItemCustoResponse | undefined; valor: string; onChange: (v: string) => void; desabilitado: boolean }) {
  const fichas = custo?.fichas ?? [];
  if (!custo || fichas.length === 0) {
    const texto = !custo ? '—' : custo.origem === 'AQUISICAO' ? 'Custo de aquisição' : 'Sem ficha técnica';
    return (
      <div aria-labelledby="r-ficha" className="flex h-10 items-center rounded-[10px] border border-input bg-surface-muted px-3 text-ink-secondary">
        <span className="truncate">{texto}</span>
      </div>
    );
  }
  return (
    <Select value={valor} onValueChange={onChange} disabled={desabilitado}>
      <SelectTrigger id="f-ficha" aria-labelledby="r-ficha" className="h-10 w-full rounded-[10px] bg-card">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={FICHA_ATIVA}>{custo.fichaAtiva ? `Versão ${custo.fichaAtiva.versao} (ativa)` : 'Sem ficha ativa'}</SelectItem>
        {fichas
          .filter((f) => !f.ativa)
          .map((f) => (
            <SelectItem key={f.id} value={f.id}>
              {`Versão ${f.versao}${f.descricao ? ` · ${f.descricao}` : ''} (só simular)`}
            </SelectItem>
          ))}
      </SelectContent>
    </Select>
  );
}
